// S1 — Dashboard del turno del Supervisor.
//
// Layout 2 columnas (refactor 2026-05-26 contra HTML de referencia Dark _ primario (5).png):
//   Header:
//     · Bloque izquierdo: "SUPERVISOR EN TURNO" + Nombre + Turno N · HH:MM — HH:MM
//     · Bloque derecho: KPIs inline (Validados / Incidencias resueltas / Pendientes / Capturas delegadas)
//   Cuerpo (grid lg:grid-cols-3):
//     · col-span-2 izquierda: "ESTADO DE LÍNEAS EN VIVO" + grid 3 cols de LineaCard
//     · col-span-1 derecha (sticky): "INCIDENCIAS ACTIVAS · N sin resolver"
//   Footer en orden:
//     · ProgramacionSemanalBanner (colapsable → dialog con tabla)
//     · ComparativaBanner (Cumpl 87% · Min 46h 12m · Paq 41.260)
//
// En mobile/tablet (<lg), las 2 columnas se apilan: líneas primero, incidencias después.
// El SupervisorSidebar NO se toca (instrucción del usuario).

import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { semaforoCumplimiento, toneTextClass } from '../../lib/semaforo';
import type {
  SupervisorIncidenciaActiva,
  SupervisorKpiLinea,
  SupervisorLinea,
  SupervisorProgramacionSemanal,
  SupervisorComparativa,
} from 'schemas';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { ConectividadBadge } from '../../components/ui/conectividad-badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Spinner } from '../../components/ui/spinner';
import { Textarea } from '../../components/ui/textarea';
import { RoleLayout } from '../../components/layout/RoleLayout';
import { SupervisorSidebar } from '../../components/layout/SupervisorSidebar';
import { useAuth } from '../../lib/auth';
import { useResolverIncidencia, useSupervisorDashboard } from '../../lib/api/queries';
import { useTenantConfig } from '../../lib/api/useTenantConfig';
import { cn } from '../../lib/utils';
import { etiquetaUnidad } from '../../lib/unidades';
import { useCorreccionesParo } from './correccionesParoQueries';
import { etiquetaLider, severidadLabel } from './etiquetas';

export function S1DashboardPage() {
  const { state, signOut } = useAuth();
  const { atribucionCruce } = useTenantConfig();
  const { data, isLoading, isError, error, refetch, isFetching, dataUpdatedAt } =
    useSupervisorDashboard();
  // Correcciones de paro de mantenimiento sin resolver — misma query que la
  // pestaña de S3 y el badge del sidebar (cacheada, poll 30s).
  const { data: correccionesParo } = useCorreccionesParo('propuesta');
  const correccionesPendientes = correccionesParo?.conteos.propuestas ?? 0;
  const [incidenciaAResolver, setIncidenciaAResolver] = useState<SupervisorIncidenciaActiva | null>(
    null,
  );
  const [programacionOpen, setProgramacionOpen] = useState(false);
  // Tiendas es una línea más de la operación (spec captura-tiendas): card en el
  // grid con el estado de validación del día, clickeable a S8.

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  if (isLoading) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 bg-surface-base p-6 text-fg">
        <Spinner className="size-6" />
        <p className="text-sm text-fg-secondary">Cargando dashboard…</p>
      </main>
    );
  }

  if (isError || !data) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-4 bg-surface-base p-6 text-center text-fg">
        <h1 className="text-lg font-medium text-crit">No pudimos cargar el dashboard</h1>
        <p className="text-sm text-fg-secondary">
          {error instanceof Error ? error.message : 'Error desconocido'}
        </p>
        <Button onClick={() => refetch()} loading={isFetching}>
          Reintentar
        </Button>
        <Button variant="ghost" size="sm" onClick={() => signOut()}>
          Cerrar sesión
        </Button>
      </main>
    );
  }

  if (data.scope.plantas_asignadas.length === 0) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-4 bg-surface-base p-6 text-center text-fg">
        <h1 className="text-xl font-medium text-fg">Sin plantas asignadas</h1>
        <p className="text-sm text-fg-secondary">
          Hola <strong className="text-fg">{data.user.nombre}</strong>. No tiene plantas asignadas
          en <strong className="text-fg">{data.tenant.nombre}</strong>.
        </p>
        <p className="text-sm text-fg-tertiary">Contacte al admin del tenant.</p>
        <Button variant="ghost" size="sm" onClick={() => signOut()}>
          Cerrar sesión
        </Button>
      </main>
    );
  }

  const segundosDesdeRefresh = dataUpdatedAt
    ? Math.max(0, Math.floor((Date.now() - dataUpdatedAt) / 1000))
    : null;
  // CTA de cierre: cuando el turno PROGRAMADO del supervisor ya terminó (según
  // hora_servidor, tz del tenant — no el reloj del dispositivo), invitarlo a
  // descargar la foto del resumen (S9). Si le quedan capturas sin validar, el
  // copy se lo recuerda: lo no validado no sale en la foto.
  const turnoFinalizado = (() => {
    const t = data.turno_objetivo;
    if (t.fuente !== 'programacion_hoy' || !t.hora_inicio || !t.hora_fin) return false;
    const ahora = data.hora_servidor;
    return t.cruza_medianoche ? ahora >= t.hora_fin && ahora < t.hora_inicio : ahora >= t.hora_fin;
  })();
  const pendientesTurno = data.kpis_agregados.capturas.pendientes;
  // Fecha OPERATIVA del turno que cerró: con atribución `dia_inicio`, para T3
  // (cruza medianoche) ya pasamos la medianoche cuando termina → la operativa
  // es AYER. Con `dia_fin` el turno que cruza cuenta para el día en que
  // TERMINA → la operativa es HOY, sin restar.
  const fechaOperativaCta = (() => {
    const t = data.turno_objetivo;
    if (
      atribucionCruce === 'dia_inicio' &&
      t.cruza_medianoche &&
      data.hora_servidor < (t.hora_inicio ?? '')
    ) {
      const [y, m, d] = data.fecha_hoy.split('-').map(Number);
      const ayer = new Date(Date.UTC(y!, m! - 1, d! - 1));
      return ayer.toISOString().slice(0, 10);
    }
    return data.fecha_hoy;
  })();
  const ctaResumenUrl = `/supervisor/resumen?fecha=${fechaOperativaCta}&turno=${data.turno_objetivo.numero ?? ''}`;
  // Lista de pendientes (S10) del turno que cerró, sobre la misma fecha operativa.
  const ctaPendientesUrl = `/supervisor/pendientes?desde=${fechaOperativaCta}&hasta=${fechaOperativaCta}&turno=${data.turno_objetivo.numero ?? ''}`;
  const plantasLabel = data.scope.plantas_asignadas.map((p) => p.nombre).join(' · ');
  // Las tienda-líneas SON líneas con KPI propio — entran al grid como cards.
  const lineasFisicas = data.lineas;
  const lineasConKpi = lineasFisicas.filter((l) => l.kpi !== null);
  const lineasSinKpi = lineasFisicas.filter((l) => l.kpi === null);

  return (
    <RoleLayout sidebar={<SupervisorSidebar />} roleLabel={`Supervisor · ${plantasLabel}`}>
      <div className="flex flex-col gap-5">
        {/* ============================================================ */}
        {/* Header                                                       */}
        {/* ============================================================ */}
        <HeaderSupervisor
          nombreSupervisor={`${data.user.nombre} ${data.user.apellido}`}
          turnoNumero={data.turno_objetivo.numero}
          turnoHoraInicio={data.turno_objetivo.hora_inicio}
          turnoHoraFin={data.turno_objetivo.hora_fin}
          turnoFuente={data.turno_objetivo.fuente}
          lineasCount={lineasFisicas.length}
          capturas={data.kpis_agregados.capturas}
          incidenciasResueltas={data.kpis_agregados.incidencias.resueltas}
        />

        {/* Banners de estado de programación */}
        {data.turno_objetivo.fuente === 'turno_actual' ? (
          <Card variant="warn-left">
            <CardContent className="p-3 pt-3 text-sm text-fg-secondary">
              <strong className="text-fg">⚠ Sin programación activa hoy.</strong> Mostrando KPI del
              turno actual (T{data.turno_objetivo.numero}) según el reloj del servidor.
            </CardContent>
          </Card>
        ) : null}
        {data.turno_objetivo.fuente === 'sin_turno' ? (
          <Card>
            <CardContent className="p-3 pt-3 text-sm text-fg-secondary">
              <strong className="text-fg">Fuera de turno.</strong> No hay turno activo en este
              momento (<span className="font-mono">{data.hora_servidor}</span>) ni programación
              cargada para hoy.
            </CardContent>
          </Card>
        ) : null}

        {/* Correcciones de paro de mantenimiento pendientes de resolución */}
        {correccionesPendientes > 0 ? (
          <Card variant="warn-left">
            <CardContent className="flex flex-wrap items-center justify-between gap-3 p-3 pt-3 text-sm text-fg-secondary">
              <span>
                <strong className="text-fg">
                  {correccionesPendientes} correcci{correccionesPendientes === 1 ? 'ón' : 'ones'} de
                  paro
                </strong>{' '}
                propuesta{correccionesPendientes === 1 ? '' : 's'} por mantenimiento por resolver.
              </span>
              <Link to="/supervisor/incidencias?tab=correcciones">
                <Button size="sm" variant="secondary" type="button">
                  Revisar
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : null}

        {/* CTA de cierre de turno: descargar la foto del resumen (S9) */}
        {turnoFinalizado ? (
          <Card variant="ok-left">
            <CardContent className="flex flex-wrap items-center justify-between gap-3 p-3 pt-3 text-sm text-fg-secondary">
              <div>
                <strong className="text-fg">Turno {data.turno_objetivo.numero} finalizado.</strong>{' '}
                {pendientesTurno > 0 ? (
                  <>
                    Le quedan{' '}
                    <Link
                      to={ctaPendientesUrl}
                      className="underline decoration-dotted underline-offset-2"
                    >
                      <strong className="text-warn">{pendientesTurno} capturas sin validar</strong>
                    </Link>{' '}
                    — lo no validado no sale en la foto del resumen.
                  </>
                ) : (
                  <>Todo validado: descargue la foto del resumen para compartir al grupo.</>
                )}
              </div>
              <Link to={pendientesTurno > 0 ? ctaPendientesUrl : ctaResumenUrl}>
                <Button size="sm" variant={pendientesTurno > 0 ? 'secondary' : 'primary'}>
                  {pendientesTurno > 0 ? 'Validar pendientes' : 'Descargar foto del turno'}
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : null}

        {/* ============================================================ */}
        {/* Cuerpo: 2 columnas en desktop, apilado en mobile/tablet     */}
        {/* ============================================================ */}
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          {/* Columna izquierda: estado de líneas */}
          <section className="flex flex-col gap-2 lg:col-span-2">
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="text-[11px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                Estado de líneas en vivo
              </h2>
              <p className="text-[11px] text-fg-tertiary">
                cada 30 segundos · refrescado hace{' '}
                {segundosDesdeRefresh !== null ? `${segundosDesdeRefresh}s` : '—'}
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {/* Tiendas es una línea más: con actividad va ENTRE las líneas con
                  KPI (mismo look que LineaCard); sin corridas, al final con las
                  "Sin captura aún hoy". */}
              {lineasConKpi.map((l) => (
                <Link
                  key={l.linea.id}
                  to={`/supervisor/linea/${l.linea.id}`}
                  className="block transition-transform hover:scale-[1.01]"
                >
                  <LineaCard item={l} programacionActiva={data.programacion_hoy.activa} />
                </Link>
              ))}
              {lineasSinKpi.map((l) => (
                <Link
                  key={l.linea.id}
                  to={`/supervisor/linea/${l.linea.id}`}
                  className="block transition-transform hover:scale-[1.01]"
                >
                  <LineaCard item={l} programacionActiva={data.programacion_hoy.activa} />
                </Link>
              ))}
            </div>
          </section>

          {/* Columna derecha: incidencias activas (sticky en desktop) */}
          <aside className="flex flex-col gap-2 lg:sticky lg:top-4 lg:self-start">
            <h2 className="flex items-baseline justify-between gap-2 text-[11px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
              <span>Incidencias activas</span>
              {data.incidencias_activas.length > 0 ? (
                <span className="text-crit">{data.incidencias_activas.length} sin resolver</span>
              ) : (
                <span className="text-ok">al día</span>
              )}
            </h2>
            <IncidenciasActivasSidebar
              incidencias={data.incidencias_activas}
              resueltasHoy={data.kpis_agregados.incidencias.resueltas}
              onResolver={(inc) => setIncidenciaAResolver(inc)}
            />
          </aside>
        </div>

        {/* ============================================================ */}
        {/* Programación semanal (banner colapsable)                     */}
        {/* ============================================================ */}
        <ProgramacionSemanalBanner
          data={data.programacion_semanal}
          onOpen={() => setProgramacionOpen(true)}
        />

        {/* ============================================================ */}
        {/* Comparativa (banner horizontal)                              */}
        {/* ============================================================ */}
        <ComparativaBanner comparativa={data.kpis_agregados.comparativa} />

        <footer className="text-center text-[11px] text-fg-tertiary">
          S1 · supervisor · ordenado por prioridad operativa
        </footer>
      </div>

      <ResolverIncidenciaDialog
        incidencia={incidenciaAResolver}
        onClose={() => setIncidenciaAResolver(null)}
      />

      <ProgramacionSemanalDialog
        open={programacionOpen}
        data={data.programacion_semanal}
        onClose={() => setProgramacionOpen(false)}
      />
    </RoleLayout>
  );
}

// =============================================================================
// HeaderSupervisor — header con bloque izquierdo + KPIs inline a la derecha
// =============================================================================

function HeaderSupervisor({
  nombreSupervisor,
  turnoNumero,
  turnoHoraInicio,
  turnoHoraFin,
  turnoFuente,
  lineasCount,
  capturas,
  incidenciasResueltas,
}: {
  nombreSupervisor: string;
  turnoNumero: number | null;
  turnoHoraInicio: string | null;
  turnoHoraFin: string | null;
  turnoFuente: 'programacion_hoy' | 'turno_actual' | 'sin_turno';
  lineasCount: number;
  capturas: { pendientes: number; validados: number; delegadas: number };
  incidenciasResueltas: number;
}) {
  const hayTurno = turnoNumero !== null && turnoHoraInicio && turnoHoraFin;
  return (
    <header className="flex flex-col gap-3 border-b border-border pb-4 lg:flex-row lg:items-end lg:justify-between">
      {/* Bloque izquierdo: label + nombre + turno */}
      <div className="flex flex-col gap-0.5">
        <p className="text-[11px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
          Supervisor en turno
        </p>
        <h1 className="text-2xl font-medium leading-tight text-fg">{nombreSupervisor}</h1>
        {hayTurno ? (
          <p className="text-sm text-fg-secondary">
            Turno {turnoNumero} · {turnoHoraInicio} — {turnoHoraFin} ·{' '}
            <span className="text-fg-tertiary">
              {lineasCount} línea{lineasCount === 1 ? '' : 's'} en turno
            </span>
          </p>
        ) : (
          <p className="text-sm text-fg-tertiary">
            {turnoFuente === 'sin_turno' ? 'Fuera de turno · ' : ''}
            {lineasCount} línea{lineasCount === 1 ? '' : 's'} en scope
          </p>
        )}
      </div>

      {/* Bloque derecho: KPIs inline (4 números chicos en columnas) */}
      <div className="grid grid-cols-4 gap-4 lg:gap-6">
        <KpiInline label="Validados" value={capturas.validados} tone="ok" />
        <KpiInline
          label="Incidencias resueltas"
          value={incidenciasResueltas}
          tone={incidenciasResueltas > 0 ? 'ok' : 'neutral'}
        />
        <KpiInline
          label="Pendientes"
          value={capturas.pendientes}
          tone={capturas.pendientes > 0 ? 'warn' : 'neutral'}
        />
        <KpiInline label="Capturas delegadas" value={capturas.delegadas} tone="info" />
      </div>
    </header>
  );
}

function KpiInline({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'ok' | 'warn' | 'crit' | 'info' | 'neutral';
}) {
  const toneClass = {
    ok: 'text-ok',
    warn: 'text-warn',
    crit: 'text-crit',
    info: 'text-info',
    neutral: 'text-fg',
  }[tone];
  return (
    <div className="flex flex-col items-start gap-0.5">
      <span className="text-[10px] uppercase tracking-wide text-fg-tertiary">{label}</span>
      <span
        className={cn('text-2xl font-medium leading-none', toneClass)}
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </span>
    </div>
  );
}

// =============================================================================
// LineaCard — % cumplimiento protagonista + estado-pill al pie + border-top
// =============================================================================

function LineaCard({
  item,
  programacionActiva,
}: {
  item: SupervisorLinea;
  programacionActiva: boolean;
}) {
  const { linea, kpi } = item;

  // Color del border-top según semáforo + paro_activo
  const topBorderClass = topBorderForSemaforo(kpi);

  return (
    <Card>
      <div className={cn('h-1 w-full', topBorderClass)} />
      <CardContent className="flex flex-col gap-2 p-4 pt-3 text-sm">
        {/* Header: nombre + código */}
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="truncate text-sm font-medium text-fg">{linea.nombre}</h3>
          <span className="text-[10px] text-fg-tertiary">{linea.codigo}</span>
        </div>

        {/* Líder */}
        {kpi?.lider ? (
          <p className="-mt-1 truncate text-[11px] text-fg-tertiary">
            {etiquetaLider(kpi.lider.nombre, kpi.lider.apellido)}
          </p>
        ) : (
          <p className="-mt-1 text-[11px] text-fg-tertiary">Sin líder asignado</p>
        )}

        {/* Cuerpo: cumplimiento gigante + subtítulo */}
        {kpi === null ? (
          <div className="py-2">
            <p className="text-3xl font-medium leading-none text-fg-tertiary">—</p>
            <p className="mt-1 text-[11px] text-fg-tertiary">Sin captura aún hoy</p>
          </div>
        ) : kpi.estado_semaforo === 'sin_data' || kpi.cumplimiento_pct === null ? (
          <div className="py-2">
            <p
              className="text-3xl font-medium leading-none text-fg-secondary"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {formatearMiles(kpi.cantidad_real)}
            </p>
            <p className="mt-1 text-[11px] text-fg-tertiary">
              {etiquetaUnidad(kpi.unidad)} · {kpi.bloques_capturados} bloque(s)
            </p>
            <p className="mt-1 text-[11px] text-warn">⚠ Falta estándar para este SKU</p>
          </div>
        ) : (
          <div className="py-1">
            <p
              className={cn(
                'text-4xl font-medium leading-none',
                cumplToneText(kpi.cumplimiento_pct),
              )}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {Math.round(kpi.cumplimiento_pct)}%
            </p>
            <p className="mt-1 truncate text-[11px] text-fg-tertiary">
              <span className="tabular-nums">{formatearMiles(kpi.cantidad_real)}</span>{' '}
              {etiquetaUnidad(kpi.unidad)}
              {kpi.sku_predominante ? <> · SKU {kpi.sku_predominante.codigo_cliente}</> : null}
            </p>
          </div>
        )}

        {/* Estado-pill al pie. La conectividad de la máquina va APARTE (nunca
            mezclada con el estado de producción); en líneas manuales no hay
            badge y el pie queda idéntico. */}
        {item.conectividad ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <EstadoPill kpi={kpi} programacionActiva={programacionActiva} />
            <ConectividadBadge
              conectividad={item.conectividad}
              ultimoLatidoAt={item.ultimo_latido_at}
            />
          </div>
        ) : (
          <EstadoPill kpi={kpi} programacionActiva={programacionActiva} />
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Border-top de color: paro_activo → crit; sin data → neutral; si no, el
 * semáforo unificado 90/70 sobre cumplimiento_pct (mismo criterio que el
 * texto del % y que el coordinador). NO usa `estado_semaforo` de la MV para
 * el color (la MV calcula con 85/60 — texto y borde quedaban en criterios
 * distintos dentro de la misma card); `estado_semaforo` sigue gobernando el
 * orden de las cards y el estado `sin_data`.
 */
function topBorderForSemaforo(kpi: SupervisorKpiLinea | null): string {
  if (kpi === null) return 'bg-border';
  if (kpi.paro_activo) return 'bg-crit';
  if (kpi.estado_semaforo === 'sin_data' || kpi.cumplimiento_pct === null) return 'bg-border';
  switch (semaforoCumplimiento(kpi.cumplimiento_pct)) {
    case 'crit':
      return 'bg-crit';
    case 'warn':
      return 'bg-warn';
    case 'ok':
      return 'bg-ok';
    default:
      return 'bg-border';
  }
}

// Semáforo unificado 90/70 (lib/semaforo.ts) — antes S1 usaba 60/85 y la
// MISMA línea al 80% se veía ámbar acá pero roja en el coordinador.
function cumplToneText(pct: number): string {
  return toneTextClass(semaforoCumplimiento(pct));
}

/**
 * Estado-pill contextual al pie de la card. Casos:
 *   - paro_activo → chip crit con duración + causal
 *   - sin captura reciente (≥70 min) → chip warn con minutos o "desde HH:MM"
 *   - captura reciente → chip ok con "hace X min"
 *   - sin captura nunca + sin programación → "Sin línea asignada" (neutral)
 *   - sin captura nunca + con programación → "Sin captura aún" (neutral)
 */
function EstadoPill({
  kpi,
  programacionActiva,
}: {
  kpi: SupervisorKpiLinea | null;
  programacionActiva: boolean;
}) {
  if (kpi === null) {
    return (
      <PillBase tone="neutral">
        {programacionActiva ? 'Sin captura aún' : 'Sin línea asignada'}
      </PillBase>
    );
  }
  if (kpi.paro_activo) {
    const min = kpi.paro_activo_minutos ?? 0;
    const causal = kpi.paro_activo_causal ?? 'sin causal';
    return (
      <PillBase tone="crit">
        ⏵ Paro {formatMinutos(min)} · {causal}
      </PillBase>
    );
  }
  if (kpi.estado_semaforo === 'amarillo' && kpi.cumplimiento_pct !== null) {
    return <PillBase tone="warn">Baja capacidad · {Math.round(kpi.cumplimiento_pct)}%</PillBase>;
  }
  if (kpi.minutos_desde_ultima_captura !== null) {
    const min = kpi.minutos_desde_ultima_captura;
    if (min >= 70) {
      return <PillBase tone="warn">Sin captura {formatMinutos(min)}</PillBase>;
    }
    if (kpi.ultimo_bloque !== null) {
      return <PillBase tone="ok">Captura ok · hace {min} min</PillBase>;
    }
  }
  return null;
}

function PillBase({
  tone,
  children,
}: {
  tone: 'ok' | 'warn' | 'crit' | 'neutral';
  children: React.ReactNode;
}) {
  const cls = {
    ok: 'bg-ok-soft text-ok',
    warn: 'bg-warn-soft text-warn',
    crit: 'bg-crit-soft text-crit',
    neutral: 'bg-surface-overlay text-fg-tertiary',
  }[tone];
  return (
    <span
      className={cn(
        'inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
        cls,
      )}
    >
      {children}
    </span>
  );
}

// =============================================================================
// IncidenciasActivasSidebar — sidebar derecho del dashboard
// =============================================================================

function IncidenciasActivasSidebar({
  incidencias,
  resueltasHoy,
  onResolver,
}: {
  incidencias: SupervisorIncidenciaActiva[];
  resueltasHoy: number;
  onResolver: (inc: SupervisorIncidenciaActiva) => void;
}) {
  if (incidencias.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-1 p-4 pt-4 text-center">
          <p className="text-sm text-fg-secondary">Sin incidencias activas</p>
          <p className="text-[11px] text-fg-tertiary">
            {resueltasHoy > 0 ? `${resueltasHoy} resuelta(s) hoy` : 'Todo en orden'}
          </p>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      {incidencias.slice(0, 6).map((inc) => (
        <Card key={inc.id} variant={severidadVariant(inc.severidad)}>
          <CardContent className="flex flex-col gap-1.5 p-3 pt-3">
            <div className="flex flex-wrap items-baseline justify-between gap-1">
              <span className="text-sm font-medium text-fg">{inc.descripcion}</span>
              <Badge tone={severidadTone(inc.severidad)}>{severidadLabel(inc.severidad)}</Badge>
            </div>
            <p className="text-[11px] text-fg-tertiary">
              {inc.linea_nombre ? `${inc.linea_nombre} · ` : ''}
              hace {formatMinutos(inc.minutos_abierta)}
            </p>
            <div className="mt-1 flex flex-wrap gap-2">
              <Button size="sm" onClick={() => onResolver(inc)}>
                Resolver
              </Button>
              {inc.linea_id ? (
                <Link to={`/supervisor/linea/${inc.linea_id}`}>
                  <Button size="sm" variant="outline" type="button">
                    Ver línea
                  </Button>
                </Link>
              ) : null}
            </div>
          </CardContent>
        </Card>
      ))}
      <Link
        to="/supervisor/incidencias"
        className="px-1 text-center text-[11px] text-fg-tertiary transition-colors hover:text-fg"
      >
        Ver todas las incidencias{' '}
        {resueltasHoy > 0 ? `(${resueltasHoy} resuelta${resueltasHoy === 1 ? '' : 's'}) ` : ''}→
      </Link>
    </div>
  );
}

function severidadVariant(sev: SupervisorIncidenciaActiva['severidad']) {
  switch (sev) {
    case 'critical':
      return 'crit-left' as const;
    case 'warning':
      return 'warn-left' as const;
    default:
      return 'default' as const;
  }
}

function severidadTone(sev: SupervisorIncidenciaActiva['severidad']) {
  switch (sev) {
    case 'critical':
      return 'crit' as const;
    case 'warning':
      return 'warn' as const;
    default:
      return 'neutral' as const;
  }
}

// =============================================================================
// ResolverIncidenciaDialog
// =============================================================================

function ResolverIncidenciaDialog({
  incidencia,
  onClose,
}: {
  incidencia: SupervisorIncidenciaActiva | null;
  onClose: () => void;
}) {
  const resolverMut = useResolverIncidencia();
  const [accion, setAccion] = useState('');
  const open = incidencia !== null;

  async function handleConfirm() {
    if (!incidencia) return;
    if (accion.trim().length < 5) {
      toast.error('Acción tomada debe tener al menos 5 caracteres');
      return;
    }
    try {
      await resolverMut.mutateAsync({
        incidenciaId: incidencia.id,
        accion_tomada: accion.trim(),
      });
      toast.success('Incidencia resuelta');
      setAccion('');
      onClose();
    } catch (err) {
      toast.error('Error al resolver', {
        description: err instanceof Error ? err.message : 'unknown',
      });
    }
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        setAccion('');
        onClose();
      }}
    >
      <DialogHeader>
        <DialogTitle>Resolver incidencia</DialogTitle>
      </DialogHeader>
      <DialogContent className="flex flex-col gap-3">
        {incidencia ? (
          <>
            <div className="rounded bg-surface-overlay p-2 text-xs text-fg-secondary">
              <strong>{incidencia.descripcion}</strong>
              {incidencia.linea_nombre ? <> · Línea {incidencia.linea_nombre}</> : null}
              <br />
              Detectada hace {formatMinutos(incidencia.minutos_abierta)}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="accion_tomada">Acción tomada</Label>
              <Textarea
                id="accion_tomada"
                value={accion}
                onChange={(e) => setAccion(e.target.value)}
                placeholder="Ej. mantenimiento ajustó la selladora y reanudó producción"
              />
              <p
                className={cn(
                  'text-xs',
                  accion.trim().length >= 5 ? 'text-ok' : 'text-fg-tertiary',
                )}
              >
                {accion.trim().length} / 5 caracteres mínimos
              </p>
            </div>
          </>
        ) : null}
      </DialogContent>
      <DialogFooter>
        <Button
          variant="outline"
          onClick={() => {
            setAccion('');
            onClose();
          }}
          disabled={resolverMut.isPending}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={resolverMut.isPending || accion.trim().length < 5}
        >
          {resolverMut.isPending ? (
            <>
              <Spinner className="border-white/30 border-t-white" /> Resolviendo…
            </>
          ) : (
            'Resolver'
          )}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// =============================================================================
// ProgramacionSemanalBanner + Dialog (banner colapsable)
// =============================================================================

function ProgramacionSemanalBanner({
  data,
  onOpen,
}: {
  data: SupervisorProgramacionSemanal;
  onOpen: () => void;
}) {
  const conData = data.items.length > 0;
  return (
    <Card>
      <CardContent className="flex flex-wrap items-center justify-between gap-2 p-3 pt-3 text-sm">
        <div className="flex flex-col">
          <span className="text-[11px] font-medium uppercase tracking-wide text-fg-tertiary">
            Programación semanal de líderes
          </span>
          <span className="text-fg">
            {data.rango_humano || data.semana_iso || 'Semana actual'}
            {conData ? null : (
              <span className="ml-2 text-fg-tertiary">· sin actividad programada</span>
            )}
          </span>
        </div>
        <button
          type="button"
          onClick={onOpen}
          disabled={!conData}
          className="text-sm font-medium text-accent disabled:text-fg-tertiary"
        >
          Ver completa →
        </button>
      </CardContent>
    </Card>
  );
}

function ProgramacionSemanalDialog({
  open,
  data,
  onClose,
}: {
  open: boolean;
  data: SupervisorProgramacionSemanal;
  onClose: () => void;
}) {
  // Agrupar por línea
  const porLinea = new Map<
    string,
    {
      linea_nombre: string;
      turnos: Array<{
        turno: number;
        lider: SupervisorProgramacionSemanal['items'][number]['lider'];
      }>;
    }
  >();
  for (const item of data.items) {
    const existing = porLinea.get(item.linea_id);
    if (existing) {
      existing.turnos.push({ turno: item.turno_numero, lider: item.lider });
    } else {
      porLinea.set(item.linea_id, {
        linea_nombre: item.linea_nombre,
        turnos: [{ turno: item.turno_numero, lider: item.lider }],
      });
    }
  }

  return (
    <Dialog open={open} onClose={onClose} className="max-w-2xl">
      <DialogHeader>
        <DialogTitle>Programación semanal · {data.rango_humano || data.semana_iso}</DialogTitle>
      </DialogHeader>
      <DialogContent>
        {porLinea.size === 0 ? (
          <p className="py-4 text-center text-sm text-fg-tertiary">
            Sin actividad programada para esta semana.
          </p>
        ) : (
          <div className="overflow-hidden rounded border border-border">
            <table className="w-full text-xs">
              <thead className="bg-surface-overlay text-[10px] uppercase tracking-wide text-fg-tertiary">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Línea</th>
                  <th className="px-3 py-2 text-left font-medium">T1</th>
                  <th className="px-3 py-2 text-left font-medium">T2</th>
                  <th className="px-3 py-2 text-left font-medium">T3</th>
                </tr>
              </thead>
              <tbody>
                {Array.from(porLinea.entries()).map(([id, info]) => {
                  const t = (numero: number) =>
                    info.turnos.find((x) => x.turno === numero)?.lider ?? null;
                  return (
                    <tr key={id} className="border-t border-border">
                      <td className="px-3 py-1.5 font-medium text-fg">{info.linea_nombre}</td>
                      <td className="px-3 py-1.5 text-fg-secondary">{fmtLider(t(1))}</td>
                      <td className="px-3 py-1.5 text-fg-secondary">{fmtLider(t(2))}</td>
                      <td className="px-3 py-1.5 text-fg-secondary">{fmtLider(t(3))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cerrar
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

function fmtLider(lider: SupervisorProgramacionSemanal['items'][number]['lider']) {
  if (!lider) return <span className="text-fg-tertiary">—</span>;
  return `${lider.nombre} ${lider.apellido[0] ?? ''}.`;
}

// =============================================================================
// ComparativaBanner — banner horizontal al pie con Cumpl/Min/Paq. En tableta
// (< lg) el título va arriba y las 3 cifras ocupan el ancho; los deltas nunca
// se parten (a 820 px se partían letra por letra).
// =============================================================================

export function ComparativaBanner({ comparativa }: { comparativa: SupervisorComparativa }) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-4 pt-4 lg:flex-row lg:items-center lg:justify-between">
        <p className="text-[11px] font-medium uppercase tracking-wide text-fg-tertiary">
          Comparativa hoy vs ayer · semana pasada
        </p>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
          <ComparativaItem
            label="Cumpl."
            value={
              comparativa.cumplimiento_hoy_pct !== null
                ? `${comparativa.cumplimiento_hoy_pct}%`
                : '—'
            }
            delta={comparativa.vs_ayer_pct}
            deltaUnit="pts hoy"
          />
          <ComparativaItem
            label="Min totales paros"
            value={formatMinutos(comparativa.paros_minutos_hoy_total)}
            delta={null}
            deltaUnit=""
          />
          <ComparativaItem
            label="Paq producidos"
            value={
              comparativa.cantidad_hoy_total !== null
                ? formatearMiles(Math.round(comparativa.cantidad_hoy_total))
                : '—'
            }
            delta={comparativa.cantidad_vs_semana_pasada_pct}
            deltaUnit="% sem ant"
          />
        </div>
      </CardContent>
    </Card>
  );
}

function ComparativaItem({
  label,
  value,
  delta,
  deltaUnit,
}: {
  label: string;
  value: string;
  delta: number | null;
  deltaUnit: string;
}) {
  const deltaTone =
    delta === null || delta === 0 ? 'text-fg-tertiary' : delta > 0 ? 'text-ok' : 'text-crit';
  const arrow = delta === null || delta === 0 ? '·' : delta > 0 ? '↑' : '↓';
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-wide text-fg-tertiary">{label}</span>
      <div className="flex items-baseline gap-2">
        <span
          className="text-xl font-medium leading-none text-fg"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {value}
        </span>
        {delta !== null ? (
          <span className={cn('whitespace-nowrap text-[11px] font-medium tabular-nums', deltaTone)}>
            {arrow} {Math.abs(delta).toFixed(1)} {deltaUnit}
          </span>
        ) : null}
      </div>
    </div>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function formatMinutos(min: number): string {
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function formatearMiles(n: number): string {
  return new Intl.NumberFormat('es-CO').format(n);
}
